import React, { useState } from 'react';
import Layout from '../../components/layout/Layout';

const DirectoryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Categories', icon: '🌐', count: 150 },
    { id: 'software', name: 'Software & Technology', icon: '💻', count: 45 },
    { id: 'packaging', name: 'Packaging Solutions', icon: '📦', count: 28 },
    { id: 'labelling', name: 'Labelling & Barcoding', icon: '🏷️', count: 32 },
    { id: 'printing', name: 'Printing Services', icon: '🖨️', count: 25 },
    { id: 'logistics', name: 'Logistics & Transportation', icon: '🚚', count: 38 },
    { id: 'warehousing', name: 'Warehousing & Storage', icon: '🏭', count: 22 },
    { id: 'marketing', name: 'Marketing & Branding', icon: '📢', count: 18 }
  ];

  const samplePartners = [
    {
      id: 1,
      name: 'TechFlow Solutions',
      category: 'software',
      description: 'Enterprise supply chain management software with AI-powered analytics and real-time tracking capabilities.',
      location: 'San Francisco, CA',
      rating: 4.8,
      verified: true
    },
    {
      id: 2,
      name: 'PackPro Industries',
      category: 'packaging',
      description: 'Sustainable packaging solutions for food, pharmaceutical, and consumer goods industries.',
      location: 'Chicago, IL',
      rating: 4.6,
      verified: true
    },
    {
      id: 3,
      name: 'LabelMaster Systems',
      category: 'labelling',
      description: 'Comprehensive labelling solutions including GS1 compliance, RFID integration, and custom design services.',
      location: 'Dallas, TX',
      rating: 4.9,
      verified: true
    },
    {
      id: 4,
      name: 'PrintWorks Pro',
      category: 'printing',
      description: 'High-quality printing services for packaging, labels, and marketing materials with quick turnaround times.',
      location: 'Los Angeles, CA',
      rating: 4.7,
      verified: true
    },
    {
      id: 5,
      name: 'LogiTech Transport',
      category: 'logistics',
      description: 'End-to-end logistics solutions including warehousing, transportation, and last-mile delivery services.',
      location: 'Atlanta, GA',
      rating: 4.5,
      verified: true
    },
    {
      id: 6,
      name: 'StoreSmart Solutions',
      category: 'warehousing',
      description: 'Intelligent warehousing systems with automated storage, retrieval, and inventory management.',
      location: 'Seattle, WA',
      rating: 4.8,
      verified: true
    }
  ];

  const filteredPartners = selectedCategory === 'all' 
    ? samplePartners 
    : samplePartners.filter(partner => partner.category === selectedCategory);

  return (
    <Layout>
      <div className="ph-page">
        <div className="ph-page-header">
          <h1 className="ph-page-title">Partner Directory</h1>
          <p className="ph-page-subtitle">
            Discover verified supply chain and industry professionals across all sectors
          </p>
        </div>

        <div className="ph-page-intro">
          <p>
            Our comprehensive directory connects you with verified professionals, companies, and 
            organizations across the entire supply chain ecosystem. Each partner has been carefully 
            vetted and certified to ensure quality and reliability.
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="text-2xl mb-2">{category.icon}</div>
                <div className="text-sm font-medium">{category.name}</div>
                <div className="text-xs text-gray-500">{category.count} partners</div>
              </button>
            ))}
          </div>
        </div>

        {/* Partners Grid */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              {selectedCategory === 'all' ? 'All Partners' : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <div className="text-sm text-gray-600">
              Showing {filteredPartners.length} of {samplePartners.length} partners
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPartners.map((partner) => (
              <div key={partner.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">{partner.name}</h3>
                    {partner.verified && (
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center mb-3">
                    <div className="flex items-center text-yellow-400">
                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className={`w-4 h-4 ${i < Math.floor(partner.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <span className="ml-2 text-sm text-gray-600">{partner.rating}</span>
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-3">{partner.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">{partner.location}</div>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-blue-800 mb-4">
            Want to be listed in our directory?
          </h3>
          <p className="text-blue-700 mb-6">
            Join our network of verified partners and connect with businesses looking for your expertise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
              Submit Your Company
            </button>
            <button className="bg-white hover:bg-gray-50 text-blue-600 font-semibold py-3 px-6 rounded-lg border border-blue-600 transition-colors duration-200">
              Learn More
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DirectoryPage;
